import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Sun, Zap, TrendingUp, Activity, Leaf, Home, Car, Lightbulb, Moon } from 'lucide-react';
import { useWeather } from '../hooks/useWeather';
import WeatherWidget from './WeatherWidget';
import WeatherAnimations from './WeatherAnimations';
import SolarChart from './SolarChart';
import EasterEggs from './EasterEggs';

const EGaugeDashboard = ({ config }) => {
  const [timeRange, setTimeRange] = useState('24h');
  const [liveData, setLiveData] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [visibleLines, setVisibleLines] = useState({});
  const [lifetimeEnergyWs, setLifetimeEnergyWs] = useState(0);
  const [oldestDataDate, setOldestDataDate] = useState(null);

  // Easter egg state
  const sunClickCount = useRef(0);
  const sunClickTimer = useRef(null);
  const moonClickCount = useRef(0);
  const moonClickTimer = useRef(null);

  const { weather, weatherTestMode } = useWeather();

  // Set page title
  useEffect(() => {
    document.title = `${config.name} - Solar Dashboard`;
  }, [config.name]);

  const getTimeRange = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    const nowDate = new Date();
    let start, end;

    switch(timeRange) {
      case 'previous-hour': {
        const hourStart = new Date(nowDate);
        hourStart.setHours(hourStart.getHours() - 1, 0, 0, 0);
        const hourEnd = new Date(hourStart);
        hourEnd.setHours(hourEnd.getHours() + 1);
        start = Math.floor(hourStart.getTime() / 1000);
        end = Math.floor(hourEnd.getTime() / 1000);
        break;
      }
      case 'yesterday': {
        const yesterdayStart = new Date(nowDate);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        yesterdayStart.setHours(0, 0, 0, 0);
        const yesterdayEnd = new Date(yesterdayStart);
        yesterdayEnd.setHours(23, 59, 59, 999);
        start = Math.floor(yesterdayStart.getTime() / 1000);
        end = Math.floor(yesterdayEnd.getTime() / 1000);
        break;
      }
      case 'last-6-hours':
        start = now - (6 * 3600);
        end = now;
        break;
      case 'last-12-hours':
        start = now - (12 * 3600);
        end = now;
        break;
      case '24h':
        start = now - (24 * 3600);
        end = now;
        break;
      case '14-days':
        start = now - (14 * 24 * 3600);
        end = now;
        break;
      case '30d':
        start = now - (30 * 24 * 3600);
        end = now;
        break;
      case '91-days':
        start = now - (91 * 24 * 3600);
        end = now;
        break;
      case 'this-month': {
        const monthStart = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1, 0, 0, 0);
        start = Math.floor(monthStart.getTime() / 1000);
        end = now;
        break;
      }
      case 'this-quarter': {
        const quarter = Math.floor(nowDate.getMonth() / 3);
        const quarterStart = new Date(nowDate.getFullYear(), quarter * 3, 1, 0, 0, 0);
        start = Math.floor(quarterStart.getTime() / 1000);
        end = now;
        break;
      }
      case 'this-year': {
        const yearStart = new Date(nowDate.getFullYear(), 0, 1, 0, 0, 0);
        start = Math.floor(yearStart.getTime() / 1000);
        end = now;
        break;
      }
      case 'previous-quarter': {
        const quarter = Math.floor(nowDate.getMonth() / 3);
        const prevQuarter = quarter === 0 ? 3 : quarter - 1;
        const prevQuarterYear = quarter === 0 ? nowDate.getFullYear() - 1 : nowDate.getFullYear();
        const prevQuarterStart = new Date(prevQuarterYear, prevQuarter * 3, 1, 0, 0, 0);
        const prevQuarterEnd = new Date(prevQuarterYear, prevQuarter * 3 + 3, 0, 23, 59, 59);
        start = Math.floor(prevQuarterStart.getTime() / 1000);
        end = Math.floor(prevQuarterEnd.getTime() / 1000);
        break;
      }
      case 'last-year':
        start = now - (365 * 24 * 3600);
        end = now;
        break;
      case 'previous-year': {
        const prevYearStart = new Date(nowDate.getFullYear() - 1, 0, 1, 0, 0, 0);
        const prevYearEnd = new Date(nowDate.getFullYear() - 1, 11, 31, 23, 59, 59);
        start = Math.floor(prevYearStart.getTime() / 1000);
        end = Math.floor(prevYearEnd.getTime() / 1000);
        break;
      }
      case 'last-5-years':
        start = now - (5 * 365 * 24 * 3600);
        end = now;
        break;
      case 'last-10-years':
        start = now - (10 * 365 * 24 * 3600);
        end = now;
        break;
      case 'all':
        // Use March 1, 2024 as the start for "All" view
        start = Math.floor(new Date('2024-03-01T00:00:00Z').getTime() / 1000);
        end = now;
        break;
      default:
        start = now - (24 * 3600);
        end = now;
    }

    return { start, end };
  }, [timeRange]);

  const fetchLifetimeEnergy = useCallback(async () => {
    try {
      const url = `${config.backendApiPath}/lifetime/${config.backendPath}`;
      const response = await fetch(url, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setLifetimeEnergyWs(data.totalEnergyWs);
    } catch (error) {
      console.error(`${config.id}: Error fetching lifetime energy:`, error);
    }
  }, [config.backendApiPath, config.backendPath, config.id]);

  const fetchOldestDataDate = useCallback(async () => {
    try {
      const url = `${config.backendApiPath}/data-range/${config.backendPath}`;
      const response = await fetch(url, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.oldestDate) {
        setOldestDataDate(new Date(data.oldestDate));
      }
    } catch (error) {
      console.error(`${config.id}: Error fetching oldest data date:`, error);
    }
  }, [config.backendApiPath, config.backendPath, config.id]);

  const fetchLatestLiveData = useCallback(async () => {
    try {
      // Fetch the last 5 minutes of data to ensure we get calculated rates
      const now = Math.floor(Date.now() / 1000);
      const start = now - 300; // 5 minutes ago
      const url = `${config.backendApiPath}/data/${config.backendPath}?start=${start}&end=${now}`;
      const response = await fetch(url, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const recentData = await response.json();

      if (recentData.length > 0) {
        // Find the most recent data point that has calculated values
        // Backend returns data sorted by timestamp ascending, so iterate backwards
        let mostRecent = null;
        for (let i = recentData.length - 1; i >= 0; i--) {
          const point = recentData[i];
          // Check if this point has at least one calculated value
          const hasCalculated = Object.values(point.registers).some(reg => reg.calculated !== undefined);
          if (hasCalculated) {
            mostRecent = point;
            break;
          }
        }

        // Fallback to last point if none have calculated values
        if (!mostRecent && recentData.length > 0) {
          mostRecent = recentData[recentData.length - 1];
        }

        if (mostRecent) {
          const liveDataFromRecent = {
            timestamp: new Date(mostRecent.timestamp),
            registers: {}
          };

          for (const [regName, regData] of Object.entries(mostRecent.registers)) {
            liveDataFromRecent.registers[regName] = {
              cumulative: regData.value,
              instantRate: regData.calculated !== undefined ? regData.calculated : 0,
              type: regData.type
            };
          }

          setLiveData(liveDataFromRecent);
        }
      }
    } catch (error) {
      console.error(`${config.id}: Error fetching latest live data:`, error);
    }
  }, [config.backendApiPath, config.backendPath, config.id]);

  const fetchHistoricalData = useCallback(async (isActive) => {
    if (isActive && !isActive.current) return;

    setLoading(true);

    try {
      const { start, end } = getTimeRange();
      const url = `${config.backendApiPath}/data/${config.backendPath}?start=${start}&end=${end}`;

      const response = await fetch(url, {
        cache: 'no-store'
      });

      if (isActive && !isActive.current) {
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const backendData = await response.json();

      if (isActive && !isActive.current) {
        return;
      }

      // Transform backend data format to chart format
      const chartData = backendData.map(point => {
        let timeFormat = {};
        if (timeRange === 'previous-hour') {
          timeFormat = { hour: '2-digit', minute: '2-digit' };
        } else if (timeRange === 'yesterday' || timeRange === '24h') {
          timeFormat = { hour: '2-digit', minute: '2-digit' };
        } else if (timeRange === '14-days' || timeRange === '30d') {
          timeFormat = { month: 'short', day: 'numeric' };
        } else if (timeRange === '91-days' || timeRange === 'this-month' || timeRange === 'this-quarter' || timeRange === 'previous-quarter') {
          timeFormat = { month: 'short', day: 'numeric' };
        } else if (timeRange === 'this-year' || timeRange === 'last-year' || timeRange === 'previous-year') {
          timeFormat = { month: 'short', year: 'numeric' };
        } else if (timeRange === 'last-5-years' || timeRange === 'last-10-years' || timeRange === 'all') {
          timeFormat = { year: 'numeric', month: 'short' };
        } else {
          timeFormat = { month: 'short', day: 'numeric', hour: '2-digit' };
        }

        const dataPoint = {
          timestamp: point.timestamp,
          time: new Date(point.timestamp).toLocaleString('en-US', timeFormat)
        };

        // Extract calculated values from backend format
        for (const [regName, regData] of Object.entries(point.registers)) {
          // Use calculated value if available, otherwise use raw value for first point
          if (regData.calculated !== undefined) {
            dataPoint[regName] = regData.calculated;
          } else if (regData.value !== undefined) {
            // For first data point without calculated rate, use 0 or skip
            dataPoint[regName] = 0;
          }
        }

        return dataPoint;
      });

      setHistoricalData(chartData);

      // Auto-populate visible lines
      setVisibleLines(currentVisible => {
        if (chartData.length > 0 && Object.keys(currentVisible).length === 0) {
          const allRegisters = Object.keys(chartData[0]).filter(k => k !== 'time' && k !== 'timestamp');
          const initialVisible = {};
          // Show default visible registers from config
          allRegisters.forEach(reg => {
            if (config.registers.defaultVisible.includes(reg)) {
              initialVisible[reg] = true;
            } else {
              initialVisible[reg] = false;
            }
          });
          return initialVisible;
        }
        return currentVisible;
      });

      // Fetch latest live data separately (always gets most recent point)
      fetchLatestLiveData();
    } catch (error) {
      console.error(`${config.id}: Error fetching historical data:`, error);

      if (isActive && !isActive.current) return;
      setHistoricalData([]);
      setVisibleLines({});
    } finally {
      if (isActive && isActive.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [config.backendApiPath, config.backendPath, config.id, config.registers.defaultVisible, timeRange, getTimeRange, fetchLatestLiveData]);

  useEffect(() => {
    const isActiveRef = { current: true };

    // Reset state on mount
    setLoading(true);
    setInitialLoadComplete(false);

    // Fetch lifetime energy and oldest data date once on mount
    fetchLifetimeEnergy();
    fetchOldestDataDate();

    // Immediate fetch without delay
    const initialFetch = async () => {
      try {
        await fetchHistoricalData(isActiveRef);
      } catch (err) {
        if (isActiveRef.current) {
          console.error(`${config.id}: Initial load error:`, err);
        }
      }
    };

    initialFetch();

    // Refresh data every 60 seconds to match backend collection interval
    const histInterval = setInterval(() => {
      if (isActiveRef.current) {
        fetchHistoricalData(isActiveRef);
        fetchLatestLiveData();
      }
    }, 60000);

    return () => {
      isActiveRef.current = false;
      clearInterval(histInterval);
    };
  }, [fetchHistoricalData, fetchLifetimeEnergy, fetchOldestDataDate, fetchLatestLiveData, config.id]);

  // Refetch data when time range changes
  useEffect(() => {
    const isActiveRef = { current: true };
    fetchHistoricalData(isActiveRef);
    return () => {
      isActiveRef.current = false;
    };
  }, [timeRange, fetchHistoricalData]);


  const toggleLine = (lineName) => {
    setVisibleLines(prev => ({
      ...prev,
      [lineName]: !prev[lineName]
    }));
  };

  const getEnergyFacts = () => {
    // Use lifetime energy for environmental impact stats
    const kWh = lifetimeEnergyWs / 3600000;

    return {
      homes: Math.round(kWh / 877),
      trees: Math.round(kWh * 0.85 / 48),
      cars: Math.round(kWh / 0.3),
      bulbs: Math.round(kWh * 10)
    };
  };

  const facts = getEnergyFacts();

  const StatCard = ({ title, value, unit, icon: Icon, color, subtitle, onClick }) => (
    <div className={`bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-lg border border-gray-200 ${onClick ? 'cursor-pointer hover:shadow-xl transition-shadow' : ''}`} onClick={onClick}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-600 text-sm font-medium">{title}</span>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="flex items-baseline">
        <span className="text-3xl font-bold text-gray-800">{value}</span>
        <span className="ml-2 text-gray-500 text-lg font-medium">{unit}</span>
      </div>
      {subtitle && (
        <div className="mt-2 text-xs text-gray-400">{subtitle}</div>
      )}
    </div>
  );

  // Get current power from most recent historical data point
  const latestData = historicalData.length > 1 ? historicalData[historicalData.length - 2] :
                     (historicalData.length > 0 ? historicalData[historicalData.length - 1] : null);

  // Get register values based on config
  const getRegisterValue = (registerName) => latestData?.[registerName] || 0;

  // Calculate total power - use Power register if available, otherwise sum inverters
  const totalPower = latestData?.[config.registers.power]
    ? getRegisterValue(config.registers.power)
    : config.registers.inverters
      ? config.registers.inverters.reduce((sum, inv) => sum + getRegisterValue(inv), 0)
      : 0;

  const totalGen = totalPower * 1000;

  // Calculate max and average stats for the timeline
  const timelineStats = useMemo(() => {
    const dataToAnalyze = historicalData;

    if (dataToAnalyze.length === 0) return { max: 0, average: 0 };

    // Calculate power for each point - use Power register if available, otherwise sum inverters
    const allPowerValues = dataToAnalyze.map(point => {
      if (point[config.registers.power] !== undefined) {
        return point[config.registers.power] || 0;
      } else if (config.registers.inverters) {
        return config.registers.inverters.reduce((sum, inv) => sum + (point[inv] || 0), 0);
      }
      return 0;
    });

    // For max, only consider positive values
    const positivePowerValues = allPowerValues.filter(val => val > 0);
    const max = positivePowerValues.length > 0 ? Math.max(...positivePowerValues) : 0;

    // For average, include all values (including zeros during night)
    const average = allPowerValues.reduce((sum, val) => sum + val, 0) / allPowerValues.length;

    return {
      max: max.toFixed(2),
      average: average.toFixed(2)
    };
  }, [historicalData, config.registers.power, config.registers.inverters]);

  // Generate register colors dynamically
  const registerColors = useMemo(() => {
    const colors = {
      'gen': { color: '#10b981', label: 'Electricity Generated' },
      'use': { color: '#ef4444', label: 'From/To Utility' },
      'Power': { color: '#10b981', label: 'Power' },
    };

    // Add inverters if configured
    if (config.registers.inverters) {
      const inverterColors = ['#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4'];
      config.registers.inverters.forEach((inv, idx) => {
        // Don't override existing labels (like 'use' -> 'From/To Utility')
        if (!colors[inv]) {
          colors[inv] = { color: inverterColors[idx % inverterColors.length], label: inv };
        } else {
          // Keep existing label, just update color if needed
          colors[inv].color = colors[inv].color || inverterColors[idx % inverterColors.length];
        }
      });
    }

    // Add DC Solar if configured
    if (config.registers.dcSolar) {
      const dcColors = ['#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4'];
      config.registers.dcSolar.forEach((dc, idx) => {
        colors[dc] = { color: dcColors[idx % dcColors.length], label: dc };
      });
    }

    // Add voltages
    if (config.registers.voltages) {
      const voltageColors = ['#3b82f6', '#60a5fa', '#93c5fd'];
      config.registers.voltages.forEach((volt, idx) => {
        colors[volt] = { color: voltageColors[idx % voltageColors.length], label: volt };
      });
    }

    // Add amperage
    if (config.registers.amperage) {
      const amperageColors = ['#f59e0b', '#fb923c', '#fdba74'];
      config.registers.amperage.forEach((amp, idx) => {
        colors[amp] = { color: amperageColors[idx % amperageColors.length], label: amp };
      });
    }

    // Add environmental sensors if configured
    if (config.registers.environmental) {
      colors['Internal Temperature'] = { color: '#ef4444', label: 'Internal Temperature' };
      colors['Internal Humidity'] = { color: '#84cc16', label: 'Internal Humidity' };
    }

    return colors;
  }, [config.registers]);


  // Render stat cards based on configuration
  const renderStatCards = useCallback((toggleStarField) => {
    const cards = [];

    // Add inverter cards if configured
    if (config.registers.inverters) {
      config.registers.inverters.forEach((inv, idx) => {
        const colors = ['text-purple-500', 'text-pink-500', 'text-orange-500', 'text-cyan-500'];

        // Get most recent value from liveData if available, otherwise use latestData
        let value = 0;
        let unit = 'kW';

        if (liveData?.registers?.[inv]) {
          value = liveData.registers[inv].instantRate || 0;
          console.log(`${inv}: ${value} (from liveData at ${liveData.timestamp})`);

          // Convert Inv 1 Power to watts
          if (inv === 'Inv 1 Power') {
            value = value * 1000;
            unit = 'W';
          }
        } else {
          value = getRegisterValue(inv);

          // Convert Inv 1 Power to watts
          if (inv === 'Inv 1 Power') {
            value = value * 1000;
            unit = 'W';
          }
        }

        cards.push(
          <StatCard
            key={inv}
            title={inv.toUpperCase()}
            value={value.toFixed(2)}
            unit={unit}
            icon={Zap}
            color={colors[idx % colors.length]}
          />
        );
      });
    }

    // Add DC Solar cards if configured
    if (config.registers.dcSolar) {
      config.registers.dcSolar.forEach((dc, idx) => {
        const colors = ['text-purple-500', 'text-pink-500', 'text-orange-500', 'text-cyan-500'];

        // Get most recent value from liveData if available, otherwise use latestData
        const value = liveData?.registers?.[dc]?.instantRate || getRegisterValue(dc);

        cards.push(
          <StatCard
            key={dc}
            title={dc.toUpperCase()}
            value={value.toFixed(2)}
            unit="kW"
            icon={Zap}
            color={colors[idx % colors.length]}
          />
        );
      });
    }

    // Add system status with moon click handler for night mode
    cards.push(
      <StatCard
        key="status"
        title="SYSTEM STATUS"
        value={totalGen > 100 ? "Active" : "Standby"}
        unit=""
        icon={totalGen > 100 ? Sun : Moon}
        color={totalGen > 100 ? "text-yellow-500" : "text-indigo-500"}
        onClick={totalGen <= 100 ? () => {
          moonClickCount.current += 1;
          if (moonClickTimer.current) clearTimeout(moonClickTimer.current);

          if (moonClickCount.current >= 3) {
            toggleStarField();
            moonClickCount.current = 0;
          } else {
            moonClickTimer.current = setTimeout(() => {
              moonClickCount.current = 0;
            }, 2000);
          }
        } : undefined}
      />
    );

    return cards;
  }, [liveData, config.registers, totalGen]);

  // Calculate grid columns based on number of cards
  const gridColsClass = useMemo(() => {
    let cardCount = 1; // System status
    if (config.registers.inverters) cardCount += config.registers.inverters.length;
    if (config.registers.dcSolar) cardCount += config.registers.dcSolar.length;

    if (cardCount <= 3) return 'sm:grid-cols-3';
    if (cardCount === 4) return 'sm:grid-cols-4';
    if (cardCount === 5) return 'sm:grid-cols-5';
    return 'sm:grid-cols-6';
  }, [config.registers]);

  // Render line toggle buttons
  const renderLineToggles = () => {
    const mainRegisters = [config.registers.power];

    if (config.registers.inverters) {
      mainRegisters.push(...config.registers.inverters);
    }
    if (config.registers.dcSolar) {
      mainRegisters.push(...config.registers.dcSolar);
    }

    const otherRegisters = [];
    if (config.registers.voltages) {
      otherRegisters.push(...config.registers.voltages);
    }
    if (config.registers.amperage) {
      otherRegisters.push(...config.registers.amperage);
    }
    if (config.registers.environmental) {
      otherRegisters.push(...config.registers.environmental);
    }

    return (
      <>
        <div className="flex items-center gap-2 mb-2">
          {mainRegisters.map(lineName => {
            const color = registerColors[lineName]?.color || '#3b82f6';
            return visibleLines[lineName] !== undefined && (
              <button
                key={lineName}
                onClick={() => toggleLine(lineName)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  visibleLines[lineName]
                    ? 'text-white shadow-md'
                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}
                style={visibleLines[lineName] ? { backgroundColor: color } : {}}
              >
                {registerColors[lineName]?.label || lineName}
              </button>
            );
          })}
        </div>

        {otherRegisters.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {otherRegisters.map(lineName => {
              const color = registerColors[lineName]?.color || '#3b82f6';
              return visibleLines[lineName] !== undefined && (
                <button
                  key={lineName}
                  onClick={() => toggleLine(lineName)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    visibleLines[lineName]
                      ? 'text-white shadow-md'
                      : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                  }`}
                  style={visibleLines[lineName] ? { backgroundColor: color } : {}}
                >
                  {registerColors[lineName]?.label || lineName}
                </button>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <EasterEggs totalGen={totalGen}>
      {({ triggerSolarFlare, toggleStarField, starFieldActive }) => (
        <div className="min-h-screen relative">
          {/* Fixed background */}
          <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 -z-10"></div>

          <WeatherAnimations weather={weather} weatherTestMode={weatherTestMode} />

          <div className="p-6 relative">

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-8 relative">
          <WeatherWidget weather={weather} weatherTestMode={weatherTestMode} />

          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className={`text-4xl font-bold bg-gradient-to-r ${config.color} bg-clip-text text-transparent pb-1`}>
              {config.name}
            </h1>
          </div>

          <p className="text-gray-600 text-lg">Real-time Solar Monitoring</p>
          <div className="flex flex-col items-center gap-1 mt-1">
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <p className="text-sm text-gray-500">
                Data updates every minute • Last update: {liveData?.timestamp?.toLocaleTimeString() || 'Loading...'}
              </p>
            </div>
            {oldestDataDate && (
              <p className="text-xs text-gray-400">
                Historical data available from {oldestDataDate.toLocaleDateString()}
              </p>
            )}
          </div>

          <div className="mt-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-6 rounded-xl shadow-lg max-w-6xl mx-auto group relative">
            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="flex-1"></div>
              <div className="text-center flex-1">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2 text-yellow-200">Lifetime Total</div>
                <div className="text-6xl font-black tracking-tight mb-2 drop-shadow-lg">
                  {(lifetimeEnergyWs / 3600000).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </div>
                <div className="text-2xl font-bold mb-1">kWh Generated</div>
                <div className="text-xs opacity-90 mt-2 bg-white bg-opacity-20 inline-block px-3 py-1 rounded-full">
                  {oldestDataDate ? `Since ${oldestDataDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Since Installation'}
                </div>
              </div>
              <div className="flex-1 flex justify-end">
                <Zap
                  className="w-20 h-20 opacity-30 transition-all duration-300 group-hover:text-yellow-300 group-hover:opacity-100 group-hover:drop-shadow-[0_0_12px_rgba(253,224,71,0.8)] group-hover:animate-pulse cursor-pointer"
                  onClick={() => {
                    sunClickCount.current += 1;
                    if (sunClickTimer.current) clearTimeout(sunClickTimer.current);

                    if (sunClickCount.current >= 5) {
                      triggerSolarFlare();
                      sunClickCount.current = 0;
                    } else {
                      sunClickTimer.current = setTimeout(() => {
                        sunClickCount.current = 0;
                      }, 2000);
                    }
                  }}
                />
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 relative z-10">
              <div className="bg-white bg-opacity-20 rounded-lg p-3 transition-all duration-300 hover:bg-opacity-30 hover:scale-105 cursor-pointer relative group/homes">
                <div className="flex items-center gap-2 mb-1">
                  <Home className="w-4 h-4 transition-all duration-300 group-hover/homes:animate-pulse" />
                  <span className="text-xs font-medium">Homes for a Month</span>
                </div>
                <div className="text-2xl font-bold">{facts.homes.toLocaleString()}</div>
                <div className="text-xs opacity-75">avg. US homes</div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/homes:opacity-100 transition-opacity duration-300 pointer-events-none z-50 w-72">
                  <div className="font-semibold mb-1">How we calculated this:</div>
                  <div className="mb-1">{(lifetimeEnergyWs / 3600000).toLocaleString()} kWh ÷ 877 kWh = {facts.homes.toLocaleString()} homes</div>
                  <div className="text-gray-300 mt-2">The average US home uses about 877 kWh per month. Your solar panels have generated enough energy to power {facts.homes.toLocaleString()} homes for an entire month!</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>

              <div className="bg-white bg-opacity-20 rounded-lg p-3 transition-all duration-300 hover:bg-opacity-30 hover:scale-105 cursor-pointer relative group/trees">
                <div className="flex items-center gap-2 mb-1">
                  <Leaf className="w-4 h-4 transition-all duration-500 group-hover/trees:scale-150 group-hover/trees:-translate-y-1" />
                  <span className="text-xs font-medium">Trees Planted</span>
                </div>
                <div className="text-2xl font-bold">{facts.trees.toLocaleString()}</div>
                <div className="text-xs opacity-75">yearly equivalent</div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/trees:opacity-100 transition-opacity duration-300 pointer-events-none z-50 w-72">
                  <div className="font-semibold mb-1">How we calculated this:</div>
                  <div className="mb-1">{(lifetimeEnergyWs / 3600000).toLocaleString()} kWh × 0.85 lbs CO₂/kWh ÷ 48 lbs/tree/year = {facts.trees.toLocaleString()} trees</div>
                  <div className="text-gray-300 mt-2">Solar energy prevents CO₂ emissions. Each kWh saves ~0.85 lbs of CO₂. One tree absorbs ~48 lbs of CO₂ per year. Your panels offset the equivalent of planting {facts.trees.toLocaleString()} trees!</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>

              <div className="bg-white bg-opacity-20 rounded-lg p-3 transition-all duration-300 hover:bg-opacity-30 hover:scale-105 cursor-pointer relative group/cars">
                <div className="flex items-center gap-2 mb-1">
                  <Car className="w-4 h-4 transition-all duration-300 group-hover/cars:translate-x-1 group-hover/cars:animate-bounce" />
                  <span className="text-xs font-medium">EV Miles</span>
                </div>
                <div className="text-2xl font-bold">{facts.cars.toLocaleString()}</div>
                <div className="text-xs opacity-75">miles driven</div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/cars:opacity-100 transition-opacity duration-300 pointer-events-none z-50 w-72">
                  <div className="font-semibold mb-1">How we calculated this:</div>
                  <div className="mb-1">{(lifetimeEnergyWs / 3600000).toLocaleString()} kWh ÷ 0.3 kWh/mile = {facts.cars.toLocaleString()} miles</div>
                  <div className="text-gray-300 mt-2">Electric vehicles use about 0.3 kWh per mile on average. Your solar energy could power an EV for {facts.cars.toLocaleString()} miles - that's like driving from New York to LA {Math.round(facts.cars / 2800)} times!</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>

              <div className="bg-white bg-opacity-20 rounded-lg p-3 transition-all duration-300 hover:bg-opacity-30 hover:scale-105 cursor-pointer relative group/bulbs">
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb className="w-4 h-4 transition-all duration-300 group-hover/bulbs:text-yellow-200 group-hover/bulbs:drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]" />
                  <span className="text-xs font-medium">LED Hours</span>
                </div>
                <div className="text-2xl font-bold">{facts.bulbs.toLocaleString()}</div>
                <div className="text-xs opacity-75">100W bulb hours</div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/bulbs:opacity-100 transition-opacity duration-300 pointer-events-none z-50 w-72">
                  <div className="font-semibold mb-1">How we calculated this:</div>
                  <div className="mb-1">{(lifetimeEnergyWs / 3600000).toLocaleString()} kWh × 10 = {facts.bulbs.toLocaleString()} hours</div>
                  <div className="text-gray-300 mt-2">A 100W (0.1 kW) LED bulb uses 0.1 kWh per hour. Your solar panels generated enough energy to power a bright LED bulb for {facts.bulbs.toLocaleString()} hours - that's {Math.round(facts.bulbs / 24 / 365)} years of continuous light!</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-xl mb-8 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-500" />
            eGauge System - Live Data & Historical Charts
            <span className="text-sm text-gray-400 font-normal ml-2">({timeRange.toUpperCase()})</span>
          </h2>

          <div className={`grid grid-cols-1 ${gridColsClass} gap-4 mb-6`}>
            {renderStatCards(toggleStarField)}
          </div>

          {/* Time Range Selector */}
          <div className="mb-4">
            <span className="text-sm font-medium text-gray-600 mb-2 block">Time Range:</span>
            <div className="flex flex-wrap gap-2">
              {[
                { value: '24h', label: 'Last 24 Hours' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: '14-days', label: 'Last 14 Days' },
                { value: '30d', label: 'Last 30 Days' },
                { value: 'this-month', label: 'This Month' },
                { value: 'this-year', label: 'This Year' },
                { value: 'all', label: 'All Time' }
              ].map(range => (
                <button
                  key={range.value}
                  onClick={() => setTimeRange(range.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    timeRange === range.value
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <span className="text-sm font-medium text-gray-600 mb-2 block">Show:</span>
            {renderLineToggles()}
          </div>

          <div>
            {loading && !initialLoadComplete ? (
              <div className="h-[500px] flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  <div className="text-gray-500 text-lg">Loading solar data...</div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <SolarChart
                  historicalData={historicalData}
                  visibleLines={visibleLines}
                  registerColors={registerColors}
                  timeRange={timeRange}
                  siteId={config.id}
                />
                <div className="mt-2 text-xs text-gray-500 text-center">
                  Drag slider to select range • Scroll to zoom • Touch gestures supported
                </div>
              </div>
            )}
          </div>
        </div>

        {liveData && (
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700">System Voltages</h3>
              {config.egaugeUrl && (
                <a
                  href={config.egaugeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  title="eGauge Control Panel"
                >
                  ⚙️
                </a>
              )}
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              {Object.entries(liveData.registers)
                .filter(([name, data]) => data.type === 'V' || name.toLowerCase().includes('voltage'))
                .sort(([nameA], [nameB]) => {
                  const orderMap = { 'L1': 1, 'L2': 2, 'L3': 3 };
                  const getOrder = (name) => {
                    if (name.includes('L1')) return orderMap['L1'];
                    if (name.includes('L2')) return orderMap['L2'];
                    if (name.includes('L3')) return orderMap['L3'];
                    return 999;
                  };
                  return getOrder(nameA) - getOrder(nameB);
                })
                .map(([name, data]) => (
                  <div key={name} className="flex-1 min-w-[200px] p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 text-center">
                    <div className="text-xs text-gray-600 mb-1">{name}</div>
                    <div className="text-xl font-bold text-blue-700">
                      {data.type === 'V'
                        ? data.instantRate.toFixed(2)
                        : (data.instantRate / 1000).toFixed(1)}V
                    </div>
                  </div>
                ))}
            </div>
            {Object.keys(liveData.registers).filter(name =>
              liveData.registers[name].type === 'V' || name.toLowerCase().includes('voltage')
            ).length === 0 && (
              <div className="text-center text-gray-500 py-4">
                No voltage data available from this location
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Subtle credit in bottom right */}
      <div className="fixed bottom-2 right-2 text-xs text-gray-400 opacity-30 hover:opacity-70 transition-opacity pointer-events-none">
        Designed and Built by Cory Fiala and Claude.AI
      </div>
    </div>
      )}
    </EasterEggs>
  );
};

export default EGaugeDashboard;
